import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../utils/axios';
import { useAuth } from './auth/AuthContext';
import * as XLSX from 'xlsx';
import { 
  Users, Download, Plus, CheckCircle, Clock 
} from 'lucide-react';

// Extracted Components
import Sidebar from './admin/Sidebar';
import StatsGrid from './admin/StatsGrid';
import InquiriesTable from './admin/InquiriesTable';
import WebControl from './admin/WebControl';
import InstitutionalStructure from './admin/InstitutionalStructure';
import LeadModal from './admin/LeadModal';
import CollegeManager from './admin/CollegeManager';
import EventManager from './admin/EventManager';
import StudentWallManager from './admin/StudentWallManager';
import NoticeManager from './admin/NoticeManager';
import BlogManager from './admin/BlogManager';

const AdminPortal = () => {
  const { logout } = useAuth();
  const location = useLocation();
  
  // Derived state from URL path
  const activePage = location.pathname.split('/').pop() || 'overview';
  
  // const [institutionalTab, setInstitutionalTab] = useState('colleges_list');
  const [loading, setLoading] = useState(true);
  const [webContent, setWebContent] = useState({});
  const [savingWeb, setSavingWeb] = useState(false);
  const [studentLeads, setStudentLeads] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newLead, setNewLead] = useState({ name: '', email: '', phone: '', college: '', program: '' });
  const [stats, setStats] = useState([]);

  // Fetch Data on Mount
  useEffect(() => {
    fetchLeads();
    fetchWebContent();
  }, []);

  // Update Stats based on CURRENTLY VIEWED leads
  useEffect(() => {
    const leadsToCalculate = activePage === 'messages' 
      ? studentLeads.filter(l => l.leadType === 'Message')
      : activePage === 'inquiries'
        ? studentLeads.filter(l => l.leadType === 'Admission')
        : studentLeads;

    const total = leadsToCalculate.length;
    const pending = leadsToCalculate.filter(l => l.status === 'New').length;
    const enrolled = leadsToCalculate.filter(l => l.status === 'Enrolled').length;
    const rate = total > 0 ? ((enrolled / total) * 100).toFixed(1) : 0;

    setStats([
      { label: activePage === 'messages' ? 'Total Messages' : 'Total Inquiries', value: total.toString(), change: `+${total}`, trend: 'up', icon: Users },
      { label: 'Pending Follow-ups', value: pending.toString(), change: pending.toString(), trend: 'down', icon: Clock },
      { label: activePage === 'messages' ? 'Resolved' : 'Enrolled', value: enrolled.toString(), change: `+${enrolled}`, trend: 'up', icon: CheckCircle },
      { label: 'Conversion Rate', value: `${rate}%`, change: `${rate}%`, trend: 'up', icon: Users }
    ]);
  }, [studentLeads, activePage]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const res = await api.get('/leads/all');
      setStudentLeads(res.data.data);
    } catch (err) {
      console.error("Failed to fetch leads", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWebContent = async () => {
    try {
      const res = await api.get('/content');
      const contentMap = {};
      const listKeys = ['colleges_list', 'placements_list', 'research_list', 'innovations_list', 'journals_list'];
      
      res.data.data.forEach(item => {
        if (listKeys.includes(item.key) && typeof item.value === 'string') {
          try {
            contentMap[item.key] = JSON.parse(item.value);
          } catch (e) {
            contentMap[item.key] = [];
          }
        } else {
          contentMap[item.key] = item.value;
        }
      });
      setWebContent(contentMap);
    } catch (err) {
      console.error("Failed to fetch web content", err);
    }
  };

  const handleUpdateWebContent = async (e) => {
    if (e) e.preventDefault();
    setSavingWeb(true);
    try {
      const updates = Object.keys(webContent).map(key => {
        let val = webContent[key];
        // If it's an array or object, stringify it before saving
        if (typeof val === 'object' && val !== null) {
          val = JSON.stringify(val);
        }
        return {
          key,
          value: val,
          section: 'general'
        };
      });
      await api.post('/content/batch', { updates });
      alert("Website updated successfully!");
    } catch (err) {
      alert("Failed to update website");
    } finally {
      setSavingWeb(false);
    }
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    try {
      const leadData = {
        ...newLead,
        leadType: activePage === 'messages' ? 'Message' : 'Admission'
      };
      await api.post('/leads/submit', leadData);
      setIsModalOpen(false);
      setNewLead({ name: '', email: '', phone: '', college: '', program: '' });
      fetchLeads();
    } catch (err) {
      alert("Failed to add record");
    }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/leads/${id}`, { status: newStatus });
      fetchLeads();
    } catch (err) {
      alert("Failed to update status");
    }
  };

  const handleDeleteLead = async (id) => {
    if (window.confirm("Are you sure you want to delete this inquiry?")) {
      try {
        await api.delete(`/leads/${id}`);
        setStudentLeads(studentLeads.filter(lead => lead._id !== id));
      } catch (err) {
        alert("Delete failed");
      }
    }
  };

  const handleExport = () => {
    if (studentLeads.length === 0) return alert("No data to export");
    const dataToExport = studentLeads.filter(l => {
      if (activePage === 'messages') return l.leadType === 'Message';
      if (activePage === 'inquiries') return l.leadType === 'Admission';
      return true;
    }).map(lead => ({
      'Type': lead.leadType,
      'Name': lead.name,
      'Email': lead.email,
      'Phone': lead.phone,
      'College': lead.college || 'N/A',
      'Program': lead.program,
      'Status': lead.status,
      'Date': new Date(lead.createdAt).toLocaleDateString()
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Inquiries");
    XLSX.writeFile(workbook, "Arya_Sabhyata_Leads.xlsx");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar activePage={activePage} logout={logout} />

      <main className="flex-1 ml-64 p-8">
        {activePage !== 'student-desk-cms' && (
          <header className="flex justify-between items-center mb-10">
            <div>
              <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">
                {activePage === 'overview' || activePage === 'admin' ? 'Institutional Overview' : 
                 activePage === 'home-cms' ? 'Home Page Management' : 
                 activePage === 'about-cms' ? 'About Page Editor' : 
                 activePage === 'gallery-cms' ? 'Gallery & Sliders' :                activePage === 'placement-page' ? 'Placement Page Editor' :
                 activePage === 'research-page' ? 'Research Page Editor' :
                 activePage === 'innovation-page' ? 'Innovation Hub Editor' :
                 activePage === 'student-desk-cms' ? 'Student Desk Portal' :
                 activePage === 'contact-cms' ? 'Contact Page Manager' :
                 activePage === 'footer-cms' ? 'Footer Content Editor' :
                 activePage === 'blog-cms' ? 'Campus Blog & Journal Editor' :
                 activePage === 'academic-links' ? 'Academic Institutions' :
                 activePage === 'placement-cms' ? 'Placement Portals' :
                 activePage === 'research-cms' ? 'Research & Development' :
                 activePage === 'journals-cms' ? 'Publication & Journals' :
                  activePage === 'innovations-cms' ? 'Innovations Hub' :
                 activePage === 'colleges' ? 'College & School Manager' :
                 activePage === 'blogs' ? 'Blog Management' :
                 activePage === 'notices' ? 'Notice Board Management' :
                 activePage === 'student-posts' ? 'Student Wall Moderation' :
                 activePage === 'institutional-setup' ? 'Institutional Structure' : 
                 activePage === 'messages' ? 'General Messages' :
                 'Admission Management'}
              </h2>
              <p className="text-slate-500 font-bold uppercase text-[10px] tracking-widest mt-1">Arya Sabhyata Group Portal</p>
            </div>
            
            {!['home-cms', 'about-cms', 'gallery-cms', 'blog-cms', 'placement-page', 'research-page', 'innovation-page', 'student-desk-cms', 'contact-cms', 'footer-cms', 'academic-links', 'placement-cms', 'research-cms', 'journals-cms', 'innovations-cms', 'institutional-setup', 'colleges', 'blogs'].includes(activePage) && (
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="bg-[#cc0000] text-white px-6 py-3 rounded-sm font-black uppercase text-xs tracking-widest flex items-center hover:bg-slate-900 transition-all shadow-xl"
                >
                  <Plus className="w-4 h-4 mr-2" /> {activePage === 'messages' ? 'Add New Message' : 'Add New Inquiry'}
                </button>
                <button 
                  onClick={handleExport}
                  className="bg-white text-slate-900 border border-slate-200 px-6 py-3 rounded-sm font-black uppercase text-xs tracking-widest flex items-center hover:bg-slate-50 transition-all shadow-sm"
                >
                  <Download className="w-4 h-4 mr-2" /> Export Excel
                </button>
              </div>
            )}
          </header>
        )}

        {activePage !== 'student-posts' && activePage !== 'blog-cms' && activePage !== 'student-desk-cms' && activePage !== 'blogs' && <StatsGrid stats={stats} />}

        {activePage === 'colleges' ? (
          <CollegeManager />
        ) : activePage === 'events' ? (
          <EventManager />
        ) : activePage === 'notices' ? (
          <NoticeManager />
        ) : activePage === 'blogs' ? (
          <BlogManager />
        ) : ['home-cms', 'about-cms', 'gallery-cms', 'blog-cms', 'placement-page', 'research-page', 'innovation-page', 'student-desk-cms', 'contact-cms', 'footer-cms', 'whatsapp-cms'].includes(activePage) ? (
          <>
            <WebControl 
                webContent={webContent} 
                setWebContent={setWebContent} 
                handleUpdateWebContent={handleUpdateWebContent} 
                savingWeb={savingWeb} 
                mode={activePage}
            />
            {activePage === 'student-desk-cms' && (
                <div>
                    <StudentWallManager />
                </div>
            )}
          </>
        ) : ['academic-links', 'placement-cms', 'research-cms', 'journals-cms', 'innovations-cms', 'institutional-setup'].includes(activePage) ? (
          <InstitutionalStructure 
            webContent={webContent}
            setWebContent={setWebContent}
            institutionalTab={
              activePage === 'placement-cms' ? 'placements_list' : 
              activePage === 'research-cms' ? 'research_list' : 
              activePage === 'journals-cms' ? 'journals_list' : 
              activePage === 'innovations-cms' ? 'innovations_list' : 
              'colleges_list'
            }
            handleUpdateWebContent={handleUpdateWebContent}
            savingWeb={savingWeb}
          />
        ) : (
          <InquiriesTable 
            studentLeads={studentLeads.filter(l => {
              if (activePage === 'messages') return l.leadType === 'Message';
              if (activePage === 'inquiries') return l.leadType === 'Admission';
              return true;
            })} 
            loading={loading} 
            handleUpdateStatus={handleUpdateStatus} 
            handleDeleteLead={handleDeleteLead} 
            mode={activePage === 'messages' ? 'Message' : 'Admission'}
          />
        )}

        <LeadModal 
          isModalOpen={isModalOpen} 
          setIsModalOpen={setIsModalOpen} 
          handleAddLead={handleAddLead} 
          newLead={newLead} 
          setNewLead={setNewLead} 
        />
      </main>
    </div>
  );
};

export default AdminPortal;